/**
 * WorkflowLogModal - Modale Cronologia Workflow Magazzino
 *
 * Utilizza il componente generico LogViewerModal con configurazione
 * specifica per i log workflow degli ordini magazzino.
 */

import React from 'react';
import {
  Clock, FileText, Mail, MessageCircle, Printer, Download, Edit,
  Package, Check, X, User, Calendar, Building2, RefreshCw
} from 'lucide-react';
import LogViewerModal from '../common/LogViewerModal';
import type { LogViewerConfig, LogEntry, LogColumn } from '../common/LogViewerModal';

interface WorkflowLogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================
// CONFIGURAZIONE AZIONI WORKFLOW
// ============================================================

const AZIONE_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  creazione_ordine: { icon: FileText, color: 'text-green-400 bg-green-500/20', label: 'Ordine creato' },
  modifica_ordine: { icon: Edit, color: 'text-amber-400 bg-amber-500/20', label: 'Ordine modificato' },
  invio_email: { icon: Mail, color: 'text-blue-400 bg-blue-500/20', label: 'Inviato via email' },
  invio_whatsapp: { icon: MessageCircle, color: 'text-emerald-400 bg-emerald-500/20', label: 'Inviato via WhatsApp' },
  invio_stampa: { icon: Printer, color: 'text-purple-400 bg-purple-500/20', label: 'Stampato' },
  invio_download: { icon: Download, color: 'text-cyan-400 bg-cyan-500/20', label: 'PDF scaricato' },
  invio_salvataggio_contatto: { icon: User, color: 'text-pink-400 bg-pink-500/20', label: 'Contatto salvato' },
  cambio_stato: { icon: RefreshCw, color: 'text-indigo-400 bg-indigo-500/20', label: 'Stato aggiornato' },
  conferma_ricezione: { icon: Check, color: 'text-teal-400 bg-teal-500/20', label: 'Confermato fornitore' },
  carico_parziale: { icon: Package, color: 'text-orange-400 bg-orange-500/20', label: 'Carico parziale' },
  carico_completo: { icon: Package, color: 'text-green-400 bg-green-500/20', label: 'Carico completato' },
  annullamento: { icon: X, color: 'text-red-400 bg-red-500/20', label: 'Ordine annullato' },
  reset_bozza: { icon: RefreshCw, color: 'text-slate-400 bg-slate-500/20', label: 'Riportato in bozza' },
};

const getAzioneConfig = (azione: string) => {
  return AZIONE_CONFIG[azione] || { icon: Clock, color: 'text-slate-400 bg-slate-500/20', label: azione };
};

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================================
// DEFINIZIONE COLONNE
// ============================================================

const columns: LogColumn[] = [
  {
    id: 'executed_at',
    label: 'Data/Ora',
    width: '140px',
    render: (log: LogEntry) => (
      <div className="flex items-center gap-1.5 text-xs">
        <Calendar className="w-3 h-3 text-slate-500" />
        <span className="text-slate-300">{formatDateTime(log.executed_at as string)}</span>
      </div>
    )
  },
  {
    id: 'azione',
    label: 'Azione',
    width: '180px',
    render: (log: LogEntry) => {
      const config = getAzioneConfig(log.azione as string);
      const Icon = config.icon;
      return (
        <div className="flex items-center gap-1.5">
          <div className={`w-6 h-6 rounded flex items-center justify-center ${config.color}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="text-white font-medium text-xs">{config.label}</span>
        </div>
      );
    }
  },
  {
    id: 'numero_ordine',
    label: 'Ordine',
    width: '120px',
    render: (log: LogEntry) => (
      <span className="text-white font-mono text-xs">{(log.numero_ordine as string) || '—'}</span>
    )
  },
  {
    id: 'fornitore_nome',
    label: 'Fornitore',
    render: (log: LogEntry) => (
      <div className="flex items-center gap-1">
        <Building2 className="w-3 h-3 text-slate-500" />
        <span className="text-slate-300 text-xs truncate max-w-[120px]">{(log.fornitore_nome as string) || '—'}</span>
      </div>
    )
  },
  {
    id: 'stato',
    label: 'Stato',
    render: (log: LogEntry) => {
      const statoPrecedente = log.stato_precedente as string;
      const statoNuovo = log.stato_nuovo as string;
      return statoPrecedente !== statoNuovo ? (
        <div className="flex items-center gap-1 text-xs">
          <span className="px-1 py-0.5 bg-slate-600 rounded text-xs">{statoPrecedente || '—'}</span>
          <span className="text-slate-500">→</span>
          <span className="px-1 py-0.5 bg-indigo-600 rounded text-xs">{statoNuovo}</span>
        </div>
      ) : (
        <span className="px-1 py-0.5 bg-slate-600 rounded text-xs">{statoNuovo}</span>
      );
    }
  },
  {
    id: 'executed_by_name',
    label: 'Operatore',
    render: (log: LogEntry) => (
      <div className="flex items-center gap-1">
        <User className="w-3 h-3 text-slate-500" />
        <span className="text-slate-300 text-xs">{log.executed_by_name as string}</span>
      </div>
    )
  },
  {
    id: 'note',
    label: 'Note',
    render: (log: LogEntry) => (
      <span className="text-slate-400 text-xs truncate max-w-[150px] block">{(log.note as string) || '—'}</span>
    )
  }
];

// ============================================================
// CONFIGURAZIONE LOG VIEWER
// ============================================================

const workflowLogConfig: LogViewerConfig = {
  title: 'Cronologia Workflow',
  subtitle: 'Storico operazioni ordini magazzino',
  fetchEndpoint: '/api/magazzino/workflow-log/all',
  cleanupEndpoint: '/api/magazzino/workflow-log/cleanup',
  cleanupDays: 7,
  columns,
  searchFields: ['numero_ordine', 'fornitore_nome', 'executed_by_name', 'note'],
  searchPlaceholder: 'Ordine, fornitore, operatore...',
  emptyMessage: 'Nessun log trovato',
  emptyIcon: Clock,
  filters: [
    {
      id: 'azione',
      label: 'Azione',
      type: 'select',
      options: Object.entries(AZIONE_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label }))
    },
    {
      id: 'operatore',
      label: 'Operatore',
      type: 'select'
    },
    {
      id: 'from_date',
      label: 'Da',
      type: 'date'
    },
    {
      id: 'to_date',
      label: 'A',
      type: 'date'
    }
  ]
};

// ============================================================
// COMPONENT
// ============================================================

const WorkflowLogModal: React.FC<WorkflowLogModalProps> = ({ isOpen, onClose }) => {
  return (
    <LogViewerModal
      isOpen={isOpen}
      onClose={onClose}
      config={workflowLogConfig}
    />
  );
};

export default WorkflowLogModal;
